import { useRef, useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import AuthedImage from "./AuthedImage";
import { PageObjectsOverlay } from "./CanvasEditor";

const UNIT_TO_PX = {
  in: 72,
  cm: 28.35,
  mm: 2.835,
  pt: 1,
  pc: 12,
  px: 1,
};

const PPI = 96; // screen px per inch at 100% zoom

function trimToInches(trim) {
  switch (trim) {
    case "5.5x8.5": return [5.5, 8.5];
    case "8.5x11": return [8.5, 11];
    case "7x10": return [7, 10];
    case "a4": return [8.27, 11.69];
    case "6x9":
    default: return [6, 9];
  }
}

function fmt(v, unit) {
  if (unit === "in" || unit === "cm") return v.toFixed(2);
  return v.toFixed(0);
}

/**
 * Build row descriptors from a page count, view mode and cover rule.
 * Each row:
 *   { id, type: "single" | "single-cover" | "spread", pages: number[] }
 */
function buildRows({ pageCount, viewMode, startFirstPageOnRight }) {
  if (viewMode === "single") {
    return Array.from({ length: pageCount }, (_, i) => ({
      id: `row-${i + 1}`,
      type: "single",
      pages: [i + 1],
    }));
  }

  // facing / continuous / presentation share the same row math.
  const rows = [];
  let p = 1;
  if (startFirstPageOnRight && pageCount >= 1) {
    rows.push({ id: "row-cover", type: "single-cover", pages: [1] });
    p = 2;
  }
  while (p <= pageCount) {
    if (p + 1 <= pageCount) {
      rows.push({ id: `row-${p}`, type: "spread", pages: [p, p + 1] });
      p += 2;
    } else {
      // last odd page → render alone as right-side single
      rows.push({ id: `row-${p}-tail`, type: "single-cover", pages: [p] });
      p += 1;
    }
  }
  return rows;
}

export default function CanvasView({
  title = "Untitled Book",
  author = "Founder",
  trim = "6x9",
  units = "in",
  zoom = 100,
  viewMode = "facing",
  pageCount = 24,
  startFirstPageOnRight = true,
  guidesEnabled = true,
  guidesLocked = false,
  fitMode = "none",            // "none" | "page" | "spread"
  onActivePageChange,
  onScrollToPageRef,
  pageImages = {},
  pages = [],                  // Phase B1: book_pages docs
  selectedObjectId = null,
  editingObjectId = null,
  onSelectObject,
  onEditObject,
  onCommitObjectText,
  onCreateFrame,
  activeTool = "select",
}) {
  const [wIn, hIn] = trimToInches(trim);

  // Force single-page on small screens.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const effectiveMode = isMobile ? "single" : viewMode;

  const rows = useMemo(
    () => buildRows({ pageCount, viewMode: effectiveMode, startFirstPageOnRight }),
    [pageCount, effectiveMode, startFirstPageOnRight]
  );

  // Viewport / pasteboard refs
  const viewportRef = useRef(null);
  const pasteboardRef = useRef(null);
  const [viewportSize, setViewportSize] = useState({ w: 800, h: 600 });
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const cr = entry.contentRect;
      setViewportSize({ w: cr.width, h: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute effective zoom
  const PASTE_PAD_X = 96;
  const PASTE_PAD_Y = 72;
  const SPINE_GAP = 0;
  const computedZoom = useMemo(() => {
    if (fitMode === "page") {
      const available = Math.max(120, viewportSize.w - PASTE_PAD_X * 2);
      return Math.max(20, Math.min(300, Math.floor((available / (wIn * PPI)) * 100)));
    }
    if (fitMode === "spread") {
      const spreadInches = effectiveMode === "single" ? wIn : wIn * 2;
      const available = Math.max(120, viewportSize.w - PASTE_PAD_X * 2);
      return Math.max(20, Math.min(300, Math.floor((available / (spreadInches * PPI)) * 100)));
    }
    return zoom;
  }, [fitMode, viewportSize.w, wIn, effectiveMode, zoom]);

  const pageW = wIn * PPI * (computedZoom / 100);
  const pageH = hIn * PPI * (computedZoom / 100);

  // Drag guides (relative to pasteboard scroll container)
  const [guides, setGuides] = useState({ h: [], v: [] });
  const [drag, setDrag] = useState(null);
  const startGuideDrag = (axis, e) => {
    if (guidesLocked) return;
    const rect = pasteboardRef.current.getBoundingClientRect();
    const sl = pasteboardRef.current.scrollLeft;
    const st = pasteboardRef.current.scrollTop;
    const value = axis === "h" ? e.clientY - rect.top + st : e.clientX - rect.left + sl;
    setDrag({ axis, value });
  };
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const rect = pasteboardRef.current.getBoundingClientRect();
      const sl = pasteboardRef.current.scrollLeft;
      const st = pasteboardRef.current.scrollTop;
      const value = drag.axis === "h" ? e.clientY - rect.top + st : e.clientX - rect.left + sl;
      setDrag((d) => ({ ...d, value }));
    };
    const onUp = () => {
      setGuides((g) => ({
        h: drag.axis === "h" ? [...g.h, drag.value] : g.h,
        v: drag.axis === "v" ? [...g.v, drag.value] : g.v,
      }));
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);
  const removeGuide = (axis, idx) => {
    if (guidesLocked) return;
    setGuides((g) => ({ ...g, [axis]: g[axis].filter((_, i) => i !== idx) }));
  };

  // IntersectionObserver → track active page
  const rowRefs = useRef({});
  useEffect(() => {
    const root = pasteboardRef.current;
    if (!root || !onActivePageChange) return;
    const io = new IntersectionObserver(
      (entries) => {
        // pick the entry with the largest intersection ratio
        let best = null;
        entries.forEach((e) => {
          if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
        });
        if (best && best.target.dataset.firstPage) {
          onActivePageChange(Number(best.target.dataset.firstPage));
        }
      },
      { root, threshold: [0.25, 0.5, 0.75] }
    );
    Object.values(rowRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [rows, onActivePageChange]);

  // Expose scroll-to-page to parent
  const scrollToPage = useCallback((pageNumber) => {
    const target = rows.find((r) => r.pages.includes(pageNumber));
    if (!target) return;
    const el = rowRefs.current[target.id];
    if (el && pasteboardRef.current) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [rows]);
  useEffect(() => {
    if (onScrollToPageRef) onScrollToPageRef(scrollToPage);
  }, [onScrollToPageRef, scrollToPage]);

  // Ruler sizes
  const totalRulerW = Math.max(2400, viewportSize.w * 3);
  const totalRulerH = Math.max(2400, viewportSize.h * 3);

  // Presentation mode shows one row at a time
  const [presIdx, setPresIdx] = useState(0);
  useEffect(() => { if (presIdx >= rows.length) setPresIdx(0); }, [rows.length, presIdx]);
  const rowsToRender = effectiveMode === "presentation" ? [rows[presIdx]].filter(Boolean) : rows;

  return (
    <div
      data-testid="editor-canvas"
      className="flex-1 min-w-0 min-h-0 relative grid"
      style={{
        background: "color-mix(in srgb, var(--pl-bg-2) 80%, transparent)",
        gridTemplateColumns: "28px 1fr",
        gridTemplateRows: "24px 1fr",
      }}
    >
      {/* Corner */}
      <div
        className="border-b border-r border-[var(--pl-border)] bg-[var(--pl-surface)]/80 backdrop-blur grid place-items-center text-[9px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] z-30"
        style={{ gridRow: "1 / 2", gridColumn: "1 / 2" }}
        data-testid="ruler-corner"
      >
        {units}
      </div>

      {/* Top ruler */}
      <div
        data-testid="ruler-top"
        onMouseDown={(e) => startGuideDrag("h", e)}
        className="border-b border-[var(--pl-border)] bg-[var(--pl-surface)]/80 backdrop-blur overflow-hidden relative cursor-row-resize z-20"
        style={{ gridRow: "1 / 2", gridColumn: "2 / 3" }}
        title="Drag down to create horizontal guide"
      >
        <RulerTicks width={totalRulerW} unit={units} orientation="h" zoom={computedZoom} />
      </div>

      {/* Left ruler */}
      <div
        data-testid="ruler-left"
        onMouseDown={(e) => startGuideDrag("v", e)}
        className="border-r border-[var(--pl-border)] bg-[var(--pl-surface)]/80 backdrop-blur overflow-hidden relative cursor-col-resize z-20"
        style={{ gridRow: "2 / 3", gridColumn: "1 / 2" }}
        title="Drag right to create vertical guide"
      >
        <RulerTicks height={totalRulerH} unit={units} orientation="v" zoom={computedZoom} />
      </div>

      {/* Pasteboard */}
      <div
        ref={(el) => { viewportRef.current = el; pasteboardRef.current = el; }}
        data-testid="canvas-pasteboard"
        className="overflow-auto relative"
        style={{ gridRow: "2 / 3", gridColumn: "2 / 3" }}
      >
        <div
          className="relative"
          style={{
            minWidth: "100%",
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: `${PASTE_PAD_Y}px`,
            padding: `${PASTE_PAD_Y}px ${PASTE_PAD_X}px`,
            width: "max-content",
            marginInline: "auto",
          }}
        >
          {rowsToRender.map((row) => (
            <PageRow
              key={row.id}
              row={row}
              pageW={pageW}
              pageH={pageH}
              spineGap={SPINE_GAP}
              units={units}
              wIn={wIn}
              hIn={hIn}
              title={title}
              author={author}
              guidesEnabled={guidesEnabled}
              registerRef={(el) => { rowRefs.current[row.id] = el; }}
              pageImages={pageImages}
              pages={pages}
              selectedObjectId={selectedObjectId}
              editingObjectId={editingObjectId}
              onSelectObject={onSelectObject}
              onEditObject={onEditObject}
              onCommitObjectText={onCommitObjectText}
              onCreateFrame={onCreateFrame}
              activeTool={activeTool}
              computedZoom={computedZoom}
            />
          ))}

          {/* Presentation pager */}
          {effectiveMode === "presentation" && rows.length > 1 && (
            <div className="sticky bottom-3 inline-flex items-center gap-2 rounded-full border border-[var(--pl-border)] bg-[var(--pl-surface)]/80 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">
              <button
                data-testid="pres-prev"
                onClick={() => setPresIdx((i) => Math.max(0, i - 1))}
                className="px-2 hover:text-[var(--pl-aqua)]"
              >Prev</button>
              <span>{presIdx + 1} / {rows.length}</span>
              <button
                data-testid="pres-next"
                onClick={() => setPresIdx((i) => Math.min(rows.length - 1, i + 1))}
                className="px-2 hover:text-[var(--pl-aqua)]"
              >Next</button>
            </div>
          )}
        </div>

        {/* Guides overlay (positioned absolutely inside scroll container) */}
        {guidesEnabled && (
          <>
            {guides.h.map((y, i) => (
              <div
                key={`h${i}`}
                data-testid={`guide-h-${i}`}
                onDoubleClick={() => removeGuide("h", i)}
                title="Double-click to remove"
                className="absolute left-0 right-0 h-px pointer-events-auto z-10"
                style={{ top: y, background: "rgba(39,230,209,0.7)", cursor: guidesLocked ? "not-allowed" : "ns-resize" }}
              />
            ))}
            {guides.v.map((x, i) => (
              <div
                key={`v${i}`}
                data-testid={`guide-v-${i}`}
                onDoubleClick={() => removeGuide("v", i)}
                title="Double-click to remove"
                className="absolute top-0 bottom-0 w-px pointer-events-auto z-10"
                style={{ left: x, background: "rgba(39,230,209,0.7)", cursor: guidesLocked ? "not-allowed" : "ew-resize" }}
              />
            ))}
            {drag && (
              <div
                className="absolute pointer-events-none z-10"
                style={
                  drag.axis === "h"
                    ? { left: 0, right: 0, top: drag.value, height: 1, background: "rgba(39,230,209,0.9)" }
                    : { top: 0, bottom: 0, left: drag.value, width: 1, background: "rgba(39,230,209,0.9)" }
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Row ---------- */
function PageRow({ row, pageW, pageH, spineGap, units, wIn, hIn, title, author, guidesEnabled, registerRef,
                  pageImages, pages, selectedObjectId, editingObjectId, onSelectObject, onEditObject,
                  onCommitObjectText, onCreateFrame, activeTool, computedZoom }) {
  const firstPage = row.pages[0];
  const editorProps = {
    pages, selectedObjectId, editingObjectId, onSelectObject, onEditObject,
    onCommitObjectText, onCreateFrame, activeTool, computedZoom,
  };
  if (row.type === "spread") {
    const [lp, rp] = row.pages;
    return (
      <div ref={registerRef} data-testid={`spread-row-${firstPage}`} data-first-page={firstPage}
           className="flex items-stretch" style={{ width: pageW * 2 + spineGap, gap: 0 }}>
        <Page w={pageW} h={pageH} number={lp} side="left" wIn={wIn} hIn={hIn} title={title} author={author}
              isCover={false} guidesEnabled={guidesEnabled} pageImages={pageImages} {...editorProps} />
        <SpineDivider />
        <Page w={pageW} h={pageH} number={rp} side="right" wIn={wIn} hIn={hIn} title={title} author={author}
              isCover={false} guidesEnabled={guidesEnabled} pageImages={pageImages} {...editorProps} />
      </div>
    );
  }
  if (row.type === "single-cover") {
    return (
      <div ref={registerRef} data-testid={`cover-row-${firstPage}`} data-first-page={firstPage}
           className="flex items-stretch justify-center" style={{ width: pageW }}>
        <Page w={pageW} h={pageH} number={firstPage} side="single" wIn={wIn} hIn={hIn} title={title} author={author}
              isCover={firstPage === 1} guidesEnabled={guidesEnabled} pageImages={pageImages} {...editorProps} />
      </div>
    );
  }
  return (
    <div ref={registerRef} data-testid={`single-row-${firstPage}`} data-first-page={firstPage}
         className="flex items-stretch justify-center" style={{ width: pageW }}>
      <Page w={pageW} h={pageH} number={firstPage} side="single" wIn={wIn} hIn={hIn} title={title} author={author}
            isCover={firstPage === 1} guidesEnabled={guidesEnabled} pageImages={pageImages} {...editorProps} />
    </div>
  );
}

function SpineDivider() {
  return <div data-testid="spine-divider" className="w-px bg-[var(--pl-aqua)]/40 shadow-[0_0_10px_var(--pl-aqua)]" />;
}

/* ---------- Page ---------- */
function Page({ w, h, number, side, wIn, hIn, title, author, isCover, guidesEnabled, pageImages,
                pages, selectedObjectId, editingObjectId, onSelectObject, onEditObject,
                onCommitObjectText, onCreateFrame, activeTool, computedZoom }) {
  const insertedAsset = pageImages?.[number];
  const bookPage = (pages || []).find((p) => p.order === number);
  const hasObjects = bookPage && (bookPage.objects || []).length > 0;
  return (
    <div
      data-testid={`page-${number}`}
      className="relative bg-white text-[#0a1a1a]"
      style={{
        width: w, height: h,
        boxShadow:
          side === "left"  ? "-2px 30px 80px -20px rgba(0,0,0,0.55)"
          : side === "right" ? "2px 30px 80px -20px rgba(0,0,0,0.55)"
          : "0 30px 80px -20px rgba(0,0,0,0.55)",
      }}
    >
      {guidesEnabled && (
        <>
          <div className="absolute -inset-1.5 border border-rose-400/40 pointer-events-none" />
          <div className="absolute inset-[7%] border border-[#2d8cff]/40 pointer-events-none" />
        </>
      )}
      {side === "left" && (
        <div className="absolute top-0 bottom-0 right-0 w-px bg-[var(--pl-aqua)]/25 pointer-events-none" />
      )}
      {side === "right" && (
        <div className="absolute top-0 bottom-0 left-0 w-px bg-[var(--pl-aqua)]/25 pointer-events-none" />
      )}

      {hasObjects ? (
        <PageObjectsOverlay
          page={bookPage}
          zoom={computedZoom}
          selectedId={selectedObjectId}
          editingId={editingObjectId}
          setEditingId={onEditObject}
          onSelect={onSelectObject}
          onCommitText={onCommitObjectText}
          onCreateFrame={(rect) => onCreateFrame?.(bookPage.id, rect)}
          activeTool={activeTool}
        />
      ) : (
        <div className="absolute inset-[7%] flex flex-col">
          {isCover ? (
            <CoverPage title={title} author={author} />
          ) : (
            <BodyPage title={title} number={number} side={side} insertedAsset={insertedAsset} />
          )}
        </div>
      )}

      <div className={`absolute bottom-2 ${
          side === "left" ? "left-3" : side === "right" ? "right-3" : "left-1/2 -translate-x-1/2"
        } text-[9px] font-mono uppercase tracking-[0.22em] text-[#526967]`}>
        {number}
      </div>
    </div>
  );
}

function CoverPage({ title, author }) {
  return (
    <div className="w-full h-full flex flex-col justify-end pb-6">
      <h1 className="font-display text-3xl font-bold leading-[1.05] text-[#071c1c]" style={{ fontFamily: "Playfair Display, serif" }}>
        {title}
      </h1>
      <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#526967]">By {author}</div>
    </div>
  );
}

function BodyPage({ title, number, side, insertedAsset }) {
  const hasAsset = !!insertedAsset;
  const fallbackUrl = !hasAsset && number <= 2
    ? "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?w=600&q=80" : null;
  const caption = hasAsset
    ? (insertedAsset.prompt || "AI-generated illustration").slice(0, 90)
    : "Fig 1 · The lever in motion";
  return (
    <>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#526967] mb-3 flex justify-between">
        <span>{side === "right" || side === "single" ? title : ""}</span>
        <span>{side === "left" ? title : ""}</span>
      </div>
      {number <= 2 && !hasAsset && (
        <h2 className="font-display text-2xl font-semibold leading-[1.1] mb-3 text-[#071c1c]" style={{ fontFamily: "Playfair Display, serif" }}>
          {number === 1 ? "The Quiet Lever" : "What This Book Will Do"}
        </h2>
      )}
      <div className="flex-1 text-[10.5px] leading-[1.6] text-[#071c1c]" style={{ fontFamily: "Manrope, sans-serif", textAlign: "justify" }}>
        {(hasAsset || fallbackUrl) && (
          <div
            data-testid={hasAsset ? `canvas-inserted-image-${number}` : "canvas-placed-image"}
            className="relative float-right ml-3 mb-2"
            style={{ width: "42%" }}
          >
            <div className="aspect-[4/3] rounded-sm overflow-hidden ring-2 ring-[var(--pl-aqua)]/70">
              {hasAsset ? (
                <AuthedImage path={insertedAsset.url} alt="" className="w-full h-full object-cover" />
              ) : (
                <img src={fallbackUrl} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="mt-0.5 text-[8px] uppercase tracking-[0.22em] text-[#526967]">
              {caption}
            </div>
          </div>
        )}
        <p className="mb-1.5">
          Most operators never get rich because they confuse motion with leverage. They run faster, say yes more, answer every email — and end the year exactly where they started. The lever is different. The lever is quiet. The lever is the one decision, made on Tuesday morning, that changes what Friday looks like for the next ten years.
        </p>
        <p className="mb-1.5">
          A book like this exists for one reason: to make the lever visible. Once you see it, you cannot un-see it. You will price differently. You will hire differently. You will stop chasing the wrong customers.
        </p>
        <p className="mb-1.5">
          The body text wraps around the placed figure in the same story flow — same paragraph style, same font, same line height, same rhythm. No mismatched line spacing. No second text box. One story, the way a real book reads.
        </p>
      </div>
    </>
  );
}

/* ---------- Rulers ---------- */
function RulerTicks({ width, height, unit, orientation, zoom = 100 }) {
  const unitPx = (UNIT_TO_PX[unit] || 72) * (PPI / 72) * (zoom / 100);
  const totalPx = orientation === "h" ? width : height;
  const totalUnits = Math.ceil(totalPx / unitPx);
  const ticks = [];
  for (let i = 0; i <= totalUnits * 8; i++) {
    const v = i / 8;
    const px = v * unitPx;
    const isMajor = i % 8 === 0;
    const isMid = i % 4 === 0;
    ticks.push({ v, px, isMajor, isMid });
  }
  if (orientation === "h") {
    return (
      <svg width={width} height={24} className="block">
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={t.px} x2={t.px} y1={t.isMajor ? 6 : t.isMid ? 12 : 16} y2={24} stroke="rgba(156, 183, 178, 0.6)" strokeWidth={1} />
            {t.isMajor && (
              <text x={t.px + 3} y={10} fontSize="9" fontFamily="JetBrains Mono, monospace" fill="rgba(156, 183, 178, 0.9)">
                {fmt(t.v, unit)}
              </text>
            )}
          </g>
        ))}
      </svg>
    );
  }
  return (
    <svg width={28} height={height} className="block">
      {ticks.map((t, i) => (
        <g key={i}>
          <line y1={t.px} y2={t.px} x1={t.isMajor ? 6 : t.isMid ? 12 : 18} x2={28} stroke="rgba(156, 183, 178, 0.6)" strokeWidth={1} />
          {t.isMajor && (
            <text x={3} y={t.px + 8} fontSize="9" fontFamily="JetBrains Mono, monospace" fill="rgba(156, 183, 178, 0.9)">
              {fmt(t.v, unit)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}
